'use client';

import { useCallback, useState } from 'react';

import { formatDayLong } from '@/lib/date';
import { DEFAULT_SHIPPING_METHOD, EMPTY_CARGO_ADDRESS } from '@/types/cargo';

import type {
  CargoAddress,
  CargoLocation,
  ShippingMethod,
} from '@/types/cargo';
import type { PreviewRow } from '../types';

export type CargoFlow = ReturnType<typeof useCargoFlow>;

/** Yan karttaki kargo önizlemesi: yalnızca doldurulmuş satırlar gösterilir. */
export const cargoPreviewRows = (
  address: CargoAddress,
  pickupDate: string | null
): PreviewRow[] => {
  const rows: PreviewRow[] = [];

  const location = [
    address.district?.name,
    address.town?.name,
    address.city?.name,
  ]
    .filter(Boolean)
    .join(' / ');
  if (location) rows.push({ label: 'Adres', value: location });

  if (address.addressLine.trim()) {
    rows.push({ label: 'Açık adres', value: address.addressLine.trim() });
  }

  if (pickupDate) {
    rows.push({ label: 'Kurye günü', value: formatDayLong(pickupDate) });
  }

  return rows;
};

/**
 * Kargo adımlarının (adres + randevu) istemci state'i.
 *
 * Bu adımlarda backend'e hiçbir şey yazılmaz; her şey son adımdaki tek POST'a
 * kadar bellekte durur. Bu yüzden kullanıcı adımlar arasında serbestçe gidip
 * gelebilir.
 */
export const useCargoFlow = () => {
  const [address, setAddress] = useState<CargoAddress>(EMPTY_CARGO_ADDRESS);
  const [pickupDate, setPickupDate] = useState<string | null>(null);
  // Tek yöntem olduğu için pratikte değişmiyor; seçim ekranı ve bu state,
  // ikinci yöntem eklendiğinde akışın hazır olması için duruyor.
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>(
    DEFAULT_SHIPPING_METHOD
  );

  // Cascade'de üst seviye değişince alt seviyeler anlamsız kalır. Randevu
  // takvimi de il+ilçeye bağlı olduğundan bu iki değişiklikte gün de düşer.
  const setCity = useCallback((city: CargoLocation | null) => {
    setAddress((prev) => ({ ...prev, city, town: null, district: null }));
    setPickupDate(null);
  }, []);

  const setTown = useCallback((town: CargoLocation | null) => {
    setAddress((prev) => ({ ...prev, town, district: null }));
    setPickupDate(null);
  }, []);

  const setDistrict = useCallback(
    (district: CargoLocation | null) =>
      setAddress((prev) => ({ ...prev, district })),
    []
  );

  const setAddressLine = useCallback(
    (addressLine: string) => setAddress((prev) => ({ ...prev, addressLine })),
    []
  );

  const reset = useCallback(() => {
    setAddress(EMPTY_CARGO_ADDRESS);
    setPickupDate(null);
    setShippingMethod(DEFAULT_SHIPPING_METHOD);
  }, []);

  return {
    address,
    pickupDate,
    shippingMethod,
    setShippingMethod,
    setCity,
    setTown,
    setDistrict,
    setAddressLine,
    setPickupDate,
    reset,
  };
};
